import { StatusChip } from '@/components/status-chip'
import { attachmentChipStatuses } from '@/constants/attachment-chip-statuses'
import { Pagination } from '@/shared/components/pagination'
import { Typography } from '@material-tailwind/react'
import {
  Body,
  Cell,
  Header,
  HeaderCell,
  HeaderRow,
  Row,
  Table
} from '@table-library/react-table-library/table'
import { useTranslation } from 'react-i18next'
import { FiEye, FiUpload } from 'react-icons/fi'
import { Link, generatePath, useSearchParams, useLocation } from 'react-router-dom'
import { isEmpty } from 'lodash'

import { usePagination } from '@table-library/react-table-library/pagination'
import { useTheme } from '@table-library/react-table-library/theme'
import { useGetEmployeeContractAttachmentsQuery } from '@/store/services'
import { LoadingRow } from '@/shared/components/tables'
import { PdfPreviewModal } from '@/shared/components/pdf-preview-modal'
import { useState } from 'react'
import { ATTACHMENT_STATUSES } from '@/constants'
import {
  getReplacementStatusChipValue,
  getVisibleAttachmentComment
} from '@/utils/attachment-replacement'

export const EmployeeContractAttachmentTable = ({ skip }) => {
  const isPdf = (content_type) => content_type === 'application/pdf'
  const formatDateDMY = (s) => {
    if (!s) return '-'
    const [y, m, d] = s.split('-')
    return `${d}-${m}-${y}`
  }
  const [pdfModal, setPdfModal] = useState({
    open: false,
    url: ''
  })
  const [searchParams, setSearchParams] = useSearchParams()
  const PER_PAGE = searchParams.get('per_page') || 10
  const INITIAL_PAGE = 1
  const { t } = useTranslation()
  const currentUrlParams = useLocation().search
  const {
    data: { employee_contract_attachments: attachments = [], meta = {} } = {},
    isFetching: isFetchingAttachments
  } = useGetEmployeeContractAttachmentsQuery(location.search, { skip: isEmpty(location.search) || skip })
  const theme = useTheme({
    Table: `
        --data-table-library_grid-template-columns: 0.5fr 2.5fr 2.5fr 2.5fr 0.5fr 3fr 0.3fr 0.3fr 0.3fr;
      `
  })

  const nodes = { nodes: attachments }

  const onPaginationChange = (_, state) => {
    const page = state.page + 1 || INITIAL_PAGE
    const perPage = searchParams.get('per_page') || PER_PAGE

    searchParams.set('page', page)
    searchParams.set('per_page', perPage)
    setSearchParams(searchParams)
  }

  const pagination = usePagination(
    nodes,
    {
      state: {
        page: searchParams.get('page') - 1 || 0,
        size: searchParams.get('per_page') || 10
      },
      onChange: onPaginationChange
    },
    {
      isServer: true
    }
  )
  return (
    <div className="flex flex-col justify-between">
      <PdfPreviewModal {...pdfModal} handler={() => setPdfModal(
        {
          open: false,
          url: ''
        }
      )} />
      <Table
        data={nodes}
        pagination={pagination}
        theme={theme}
        layout={{ custom: true }}
      >
        {(tableList) => (
          <>
            <Header>
              <HeaderRow>
                <HeaderCell>Módulo</HeaderCell>
                <HeaderCell>Documento</HeaderCell>
                <HeaderCell>Información asociada</HeaderCell>
                <HeaderCell>{t('contract.one')}</HeaderCell>
                <HeaderCell>Estado</HeaderCell>
                <HeaderCell>Comentarios</HeaderCell>
                <HeaderCell>Periodo</HeaderCell>
                <HeaderCell>Vencimiento</HeaderCell>
                <HeaderCell>Acciones</HeaderCell>
              </HeaderRow>
            </Header>

            <Body>
              {
                isFetchingAttachments && <LoadingRow gridColumnEnd={9} />
              }
              {
                tableList.map((attachment) => {
                  const replacementStatusChipValue = getReplacementStatusChipValue(attachment)
                  const recurrence =
                    attachment?.attachmentType?.recurrence ||
                    attachment?.attachment_type?.recurrence ||
                    attachment?.recurrence

                  const hideUpload =
                    attachment?.openReplacementCandidate?.id ||
                    attachment?.status === ATTACHMENT_STATUSES.notApplicable ||
                    (
                      attachment?.status === ATTACHMENT_STATUSES.approved &&
                      ['termination', 'monthly'].includes(recurrence)
                    )

                  return (
                    <Row key={attachment.id} item={attachment}>
                      <Cell>{attachment.module}</Cell>
                      <Cell>
                        <div className='overflow-hidden text-wrap'>
                          {attachment.attachmentType.name}
                        </div>
                      </Cell>
                      <Cell style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        <div className='flex flex-col overflow-hidden break-words whitespace-normal leading-snug'>
                          <span className='text-sm font-normal text-blue-gray-800'>
                            {attachment.associatedInfo?.name}
                          </span>
                          <span className='text-sm font-normal text-blue-gray-800'>
                            {attachment.associatedInfo?.documentNumber}
                          </span>
                        </div>
                      </Cell>
                      <Cell style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        <div className='flex flex-col overflow-hidden break-words whitespace-normal leading-snug text-blue-gray-800 text-sm'>
                          <span>
                            {attachment.associatedInfo?.contractorContractId} - {attachment.associatedInfo?.contractName}
                          </span>
                          <span>{attachment.associatedInfo?.companyName}</span>
                          {attachment.associatedInfo?.expiredContract && attachment.associatedInfo?.unlinkDate && (
                            <span className='text-xs text-red-900 mt-1'>
                              Fecha de término: {formatDateDMY(attachment.associatedInfo.unlinkDate)}
                            </span>
                          )}
                        </div>
                      </Cell>
                      <Cell>
                        <div className="flex flex-col gap-1">
                          <StatusChip
                            options={attachmentChipStatuses}
                            value={attachment.status}
                          />
                          {attachment.aboutToExpire && (
                            <StatusChip
                              options={attachmentChipStatuses}
                              value="about_to_expire"
                            />
                          )}
                          {replacementStatusChipValue && (
                            <StatusChip
                              options={attachmentChipStatuses}
                              value={replacementStatusChipValue}
                            />
                          )}
                        </div>
                      </Cell>
                      <Cell class='px-4 py-2'>{getVisibleAttachmentComment(attachment)}</Cell>
                      <Cell>{attachment.period}</Cell>
                      <Cell>{attachment.expirationDate ? formatDateDMY(attachment.expirationDate) : '-'}</Cell>
                      <Cell>
                        <div className='flex gap-2'>
                          {!hideUpload && (
                            <Link
                              to={generatePath(
                                `/attachments/:id/upload_employee_contract_attachment${currentUrlParams}`,
                                { id: attachment.id }
                              )}
                            >
                              <FiUpload />
                            </Link>
                          )}

                          {attachment?.documentInfo?.url &&

                            <FiEye className='cursor-pointer' onClick={() => {
                              isPdf(attachment?.documentInfo?.content_type)
                                ? setPdfModal({
                                  open: true,
                                  url: attachment.documentInfo?.url
                                })
                                : window.open(attachment?.documentInfo?.url, '_blank').focus()
                            }} />
                          }
                        </div>
                      </Cell>
                    </Row>
                  )
                })
              }
            </Body>
          </>
        )}
      </Table>
      <Pagination
        onPageChange={({ selected }) => pagination.fns.onSetPage(selected)}
        page={pagination.state.page}
        totalPage={meta?.pagination?.total_pages}
      />
    </div>
  )
}
